import React, { useState, useEffect } from 'react';
import {
  X,
  Filter,
  Calendar,
  SortAsc,
  SortDesc,
  RotateCcw,
  Check,
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const AdvancedFilterModal = ({
  isOpen,
  onClose,
  onApply,
  filters = {},
  sortConfig = {},
  filterOptions = [],
  sortOptions = [],
  title = 'Advanced Filters',
  minDate = null,
}) => {
  const [localFilters, setLocalFilters] = useState({});
  const [localSort, setLocalSort] = useState({ sortBy: '', sortOrder: 'DESC' });
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
      setLocalSort({
        sortBy: sortConfig.sortBy || '',
        sortOrder: sortConfig.sortOrder || 'DESC',
      });
      setDateRange([
        filters.startDate ? new Date(filters.startDate) : null,
        filters.endDate ? new Date(filters.endDate) : null,
      ]);
    }
  }, [isOpen, filters, sortConfig]);

  const handleFilterChange = (key, value) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setLocalFilters({});
    setLocalSort({ sortBy: '', sortOrder: 'DESC' });
    setDateRange([null, null]);
  };

  const handleApply = () => {
    const appliedFilters = {
      ...localFilters,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
    };
    onApply(appliedFilters, localSort);
    onClose();
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    Object.values(localFilters).forEach((val) => {
      if (val && val !== 'all' && val !== '') count++;
    });
    if (startDate || endDate) count++;
    if (localSort.sortBy) count++;
    return count;
  };

  if (!isOpen) return null;

  return (
    <div className="filter-modal-overlay" onClick={onClose}>
      <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
        <div className="filter-modal-header">
          <div className="filter-modal-title">
            <Filter size={20} />
            <h3>{title}</h3>
            {getActiveFiltersCount() > 0 && (
              <span className="active-filters-badge">{getActiveFiltersCount()}</span>
            )}
          </div>
          <button className="filter-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="filter-modal-body">
          {/* Date Range Section */}
          <div className="filter-section">
            <div className="filter-section-header">
              <Calendar size={18} />
              <span>Date Range</span>
            </div>
            <div className="filter-section-content">
              <div className="date-range-picker">
                <div className="date-input-group">
                  <label>From</label>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setDateRange([date, endDate])}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    minDate={minDate}
                    placeholderText="Start date"
                    className="date-input"
                    dateFormat="MMM dd, yyyy"
                    isClearable
                  />
                </div>
                <div className="date-input-group">
                  <label>To</label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setDateRange([startDate, date])}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate || minDate}
                    placeholderText="End date"
                    className="date-input"
                    dateFormat="MMM dd, yyyy"
                    isClearable
                  />
                </div>
              </div>
              <div className="quick-date-buttons">
                <button
                  className="quick-date-btn"
                  onClick={() => {
                    const today = new Date();
                    setDateRange([today, today]);
                  }}
                >
                  Today
                </button>
                <button
                  className="quick-date-btn"
                  onClick={() => {
                    const today = new Date();
                    const weekAgo = new Date(today);
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    setDateRange([weekAgo, today]);
                  }}
                >
                  Last 7 days
                </button>
                <button
                  className="quick-date-btn"
                  onClick={() => {
                    const today = new Date();
                    const monthAgo = new Date(today);
                    monthAgo.setDate(monthAgo.getDate() - 30);
                    setDateRange([monthAgo, today]);
                  }}
                >
                  Last 30 days
                </button>
                <button
                  className="quick-date-btn"
                  onClick={() => {
                    const today = new Date();
                    const yearStart = new Date(today.getFullYear(), 0, 1);
                    setDateRange([yearStart, today]);
                  }}
                >
                  This year
                </button>
              </div>
            </div>
          </div>

          {/* Filter Options Section */}
          {filterOptions.length > 0 && (
            <div className="filter-section">
              <div className="filter-section-header">
                <Filter size={18} />
                <span>Filter By</span>
              </div>
              <div className="filter-section-content">
                <div className="filter-options-grid">
                  {filterOptions.map((option) => (
                    <div key={option.key} className="filter-option">
                      <label>{option.label}</label>
                      {option.type === 'select' ? (
                        <select
                          value={localFilters[option.key] || 'all'}
                          onChange={(e) => handleFilterChange(option.key, e.target.value)}
                        >
                          <option value="all">All</option>
                          {option.options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : option.type === 'range' ? (
                        <div className="range-inputs">
                          <input
                            type="number"
                            placeholder="Min"
                            value={localFilters[`${option.key}Min`] || ''}
                            onChange={(e) =>
                              handleFilterChange(`${option.key}Min`, e.target.value)
                            }
                          />
                          <span>to</span>
                          <input
                            type="number"
                            placeholder="Max"
                            value={localFilters[`${option.key}Max`] || ''}
                            onChange={(e) =>
                              handleFilterChange(`${option.key}Max`, e.target.value)
                            }
                          />
                        </div>
                      ) : (
                        <input
                          type="text"
                          placeholder={option.placeholder || ''}
                          value={localFilters[option.key] || ''}
                          onChange={(e) => handleFilterChange(option.key, e.target.value)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Sort Section */}
          {sortOptions.length > 0 && (
            <div className="filter-section">
              <div className="filter-section-header">
                {localSort.sortOrder === 'ASC' ? <SortAsc size={18} /> : <SortDesc size={18} />}
                <span>Sort By</span>
              </div>
              <div className="filter-section-content">
                <div className="sort-options">
                  <div className="sort-field">
                    <label>Field</label>
                    <select
                      value={localSort.sortBy}
                      onChange={(e) =>
                        setLocalSort((prev) => ({ ...prev, sortBy: e.target.value }))
                      }
                    >
                      <option value="">Default</option>
                      {sortOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sort-direction">
                    <label>Order</label>
                    <div className="sort-direction-buttons">
                      <button
                        className={`sort-dir-btn ${localSort.sortOrder === 'ASC' ? 'active' : ''}`}
                        onClick={() => setLocalSort((prev) => ({ ...prev, sortOrder: 'ASC' }))}
                      >
                        <SortAsc size={16} />
                        Ascending
                      </button>
                      <button
                        className={`sort-dir-btn ${localSort.sortOrder === 'DESC' ? 'active' : ''}`}
                        onClick={() => setLocalSort((prev) => ({ ...prev, sortOrder: 'DESC' }))}
                      >
                        <SortDesc size={16} />
                        Descending
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="filter-modal-footer">
          <button className="btn-reset" onClick={handleReset}>
            <RotateCcw size={16} />
            Reset All
          </button>
          <div className="filter-modal-actions">
            <button className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleApply}>
              <Check size={16} />
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .filter-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .filter-modal {
          background: var(--surface);
          border-radius: var(--r-md);
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }

        .filter-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid var(--line);
          background: var(--bg-inset);
        }

        .filter-modal-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .filter-modal-title h3 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--ink);
        }

        .active-filters-badge {
          background: var(--brand-600);
          color: white;
          font-size: 0.75rem;
          padding: 0.125rem 0.5rem;
          border-radius: var(--r-pill);
          font-weight: 500;
        }

        .filter-modal-close {
          background: none;
          border: none;
          color: var(--t-500);
          cursor: pointer;
          padding: 0.375rem;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .filter-modal-close:hover {
          background: var(--line);
          color: var(--ink);
        }

        .filter-modal-body {
          flex: 1;
          overflow-y: auto;
          padding: 1.5rem;
        }

        .filter-section {
          margin-bottom: 1.5rem;
        }

        .filter-section:last-child {
          margin-bottom: 0;
        }

        .filter-section-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
          color: var(--t-700);
          font-weight: 600;
          font-size: 0.9rem;
        }

        .filter-section-content {
          background: var(--bg-inset);
          border-radius: 8px;
          padding: 1rem;
        }

        .date-range-picker {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 0.75rem;
        }

        .date-input-group label {
          display: block;
          font-size: 0.8rem;
          color: var(--t-500);
          margin-bottom: 0.25rem;
        }

        .date-input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--line-strong);
          border-radius: 6px;
          font-size: 0.9rem;
        }

        .date-input:focus {
          outline: none;
          border-color: var(--brand-600);
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
        }

        .quick-date-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .quick-date-btn {
          padding: 0.375rem 0.75rem;
          border: 1px solid var(--line-strong);
          background: var(--surface);
          border-radius: 6px;
          font-size: 0.8rem;
          color: var(--t-700);
          cursor: pointer;
          transition: all 0.2s;
        }

        .quick-date-btn:hover {
          border-color: var(--brand-600);
          color: var(--brand-600);
          background: var(--brand-100);
        }

        .filter-options-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        @media (max-width: 500px) {
          .filter-options-grid {
            grid-template-columns: 1fr;
          }
          .date-range-picker {
            grid-template-columns: 1fr;
          }
          .filter-modal-footer{
            display: inline-block !important;
          }
          .sort-options{
            display: inline !important;
          }
          .sort-direction-buttons{
            display: inline-grid !important;
            width:100%;
          }
        }

        .filter-option label {
          display: block;
          font-size: 0.8rem;
          color: var(--t-500);
          margin-bottom: 0.25rem;
        }

        .filter-option select,
        .filter-option input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--line-strong);
          border-radius: 6px;
          font-size: 0.9rem;
          background: var(--surface);
        }

        .filter-option select:focus,
        .filter-option input:focus {
          outline: none;
          border-color: var(--brand-600);
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
        }

        .range-inputs {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .range-inputs input {
          width: 100%;
        }

        .range-inputs span {
          color: var(--t-500);
          font-size: 0.8rem;
        }

        .sort-options {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .sort-field label,
        .sort-direction label {
          display: block;
          font-size: 0.8rem;
          color: var(--t-500);
          margin-bottom: 0.25rem;
        }

        .sort-field select {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--line-strong);
          border-radius: 6px;
          font-size: 0.9rem;
          background: var(--surface);
        }

        .sort-direction-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .sort-dir-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.375rem;
          padding: 0.5rem;
          border: 1px solid var(--line-strong);
          background: var(--surface);
          border-radius: 6px;
          font-size: 0.8rem;
          color: var(--t-700);
          cursor: pointer;
          transition: all 0.2s;
        }

        .sort-dir-btn:hover {
          border-color: var(--brand-600);
        }

        .sort-dir-btn.active {
          background: var(--brand-600);
          color: white;
          border-color: var(--brand-600);
        }

        .filter-modal-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          border-top: 1px solid var(--line);
          background: var(--bg-inset);
        }

        .btn-reset {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.5rem 1rem;
          border: none;
          background: transparent;
          color: var(--t-500);
          font-size: 0.9rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .btn-reset:hover {
          background: var(--line);
          color: var(--t-700);
        }

        .filter-modal-actions {
          display: flex;
          gap: 0.75rem;
        }

        .filter-modal-actions .btn-secondary {
          padding: 0.5rem 1rem;
          border: 1px solid var(--line-strong);
          background: var(--surface);
          color: var(--t-700);
          border-radius: 6px;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .filter-modal-actions .btn-secondary:hover {
          background: var(--bg-inset);
        }

        .filter-modal-actions .btn-primary {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.5rem 1rem;
          border: none;
          background: var(--brand-600);
          color: white;
          border-radius: 6px;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .filter-modal-actions .btn-primary:hover {
          background: var(--brand-700);
        }
      `}</style>
    </div>
  );
};

export default AdvancedFilterModal;
